import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile, fileUrl, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU, NAV_ORTU } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Trash2, Upload, FileText, X } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import { SEMESTER_OPTIONS, TAHUN_AJARAN_OPTIONS } from "./_shared";

const RAPOR_SEMESTER_OPTS = [
      { v: "tengah_semester_1", label: "Tengah Semester I" },
      { v: "akhir_semester_1", label: "Akhir Semester I" },
      { v: "tengah_semester_2", label: "Tengah Semester II" },
      { v: "akhir_semester_2", label: "Akhir Semester II" },
    ];
    const raporSemesterLabel = (v) => {
      // Legacy: "I" -> "Semester I", "II" -> "Semester II"
      if (v === "I") return "Semester I";
      if (v === "II") return "Semester II";
      return RAPOR_SEMESTER_OPTS.find((s) => s.v === v)?.label || v;
    };
    const RAPOR_F_EMPTY = { siswa: "", tahun_ajaran: "", semester: "" };

export function RaporGuru() {
  const [all, setAll] = useState([]);
  const [pend, setPend] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ siswa_id: "", tahun_ajaran: "2025/2026", semester: "tengah_semester_1", file_id: null });
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState(RAPOR_F_EMPTY);
  const [applied, setApplied] = useState(RAPOR_F_EMPTY);

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: p }] = await Promise.all([api.get("/rapor"), api.get("/pendampingan")]);
      setAll(r); setPend(p);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((r) => {
    if (applied.siswa && !(r.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.tahun_ajaran && r.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && r.semester !== applied.semester) return false;
    return true;
  });

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setForm({ ...form, file_id: r.id }); toast.success("Rapor diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_id) return toast.error("Upload file rapor dulu");
    try { await api.post("/rapor", form); toast.success("Rapor tersimpan"); setShow(false); setForm({ siswa_id: "", tahun_ajaran: "2025/2026", semester: "tengah_semester_1", file_id: null }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus rapor?")) return; await api.delete(`/rapor/${id}`); toast.success("Dihapus"); load(); };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Rapor Siswa" action={
        <button onClick={() => setShow(true)} className="pp-btn-primary" data-testid="open-rapor">
          <Plus className="w-4 h-4" /> Upload Rapor
        </button>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(RAPOR_F_EMPTY); setApplied(RAPOR_F_EMPTY); }}
        dataTestidPrefix="g-rapor-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari siswa" },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN_OPTIONS },
          { key: "semester", type: "select", label: "Semester", options: RAPOR_SEMESTER_OPTS },
        ]}
      />

      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold">Upload Rapor</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="pp-label">Siswa *</label>
              <select required className="pp-input" value={form.siswa_id} onChange={(e) => setForm({ ...form, siswa_id: e.target.value })} data-testid="rapor-siswa">
                <option value="">Pilih siswa</option>{pend.map((p) => <option key={p.siswa_id} value={p.siswa_id}>{p.nama_siswa}</option>)}
              </select></div>
            <div><label className="pp-label">Tahun Ajaran</label>
              <select className="pp-input" value={form.tahun_ajaran} onChange={(e) => setForm({ ...form, tahun_ajaran: e.target.value })}>
                {TAHUN_AJARAN_OPTIONS.map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className="pp-label">Semester</label>
              <select className="pp-input" value={form.semester} onChange={(e) => setForm({ ...form, semester: e.target.value })}>
                {RAPOR_SEMESTER_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></div>
            <div className="sm:col-span-2"><label className="pp-label">File Rapor (PDF) *</label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.file_id ? "Ganti File" : "Pilih PDF")}
                <input type="file" accept=".pdf" onChange={upload} className="hidden" /></label>
              {form.file_id && <span className="ml-3 text-sm text-emerald-600">✓ File siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-rapor">Simpan Rapor</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Siswa</th>
            <th className="text-left px-5 py-3">Periode</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-left px-5 py-3">Tanggal Upload</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Belum ada rapor.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{r.nama_siswa}</td>
                <td className="px-5 py-3">{r.tahun_ajaran} · {raporSemesterLabel(r.semester)}</td>
                <td className="px-5 py-3"><a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs"><FileText className="w-3.5 h-3.5 inline" /> Lihat Rapor</a></td>
                <td className="px-5 py-3 text-xs text-slate-500">{formatTanggal(r.created_at)}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => del(r.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

export function RaporOrtu() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(RAPOR_F_EMPTY);
  const [applied, setApplied] = useState(RAPOR_F_EMPTY);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/rapor"); setAll(data); }
      catch (e) { console.error(e); }
    })();
  }, []);
  const items = all.filter((r) => {
    if (applied.tahun_ajaran && r.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && r.semester !== applied.semester) return false;
    return true;
  });
  return (
    <Layout nav={NAV_ORTU}>
      <PageTitle title="Rapor Anak" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(RAPOR_F_EMPTY); setApplied(RAPOR_F_EMPTY); }}
        dataTestidPrefix="ortu-rapor-filter"
        fields={[
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN_OPTIONS },
          { key: "semester", type: "select", label: "Semester", options: RAPOR_SEMESTER_OPTS },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Periode</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-left px-5 py-3">Tanggal Terbit</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-slate-400 italic">Belum ada rapor.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{r.tahun_ajaran} · {raporSemesterLabel(r.semester)}</td>
                <td className="px-5 py-3"><a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-sm"><FileText className="w-4 h-4" /> Lihat & Download Rapor</a></td>
                <td className="px-5 py-3 text-xs text-slate-500">{formatTanggal(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
